import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Headphones, Mail, MapPin, Phone, Send, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { useBrand } from '../contexts/BrandContext';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
    const brand = useBrand();
    const [form, setForm] = useState({ name: '', email: '', subject: 'General Inquiry', message: '' });
    const [honeypot, setHoneypot] = useState('');
    const [formLoadTime] = useState(Date.now());
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (honeypot) return; // Bot filled the hidden field
        if (Date.now() - formLoadTime < 3000) { toast.error('Please take a moment before submitting'); return; } // Too fast = bot
        if (!form.name || !form.email || !form.message) { toast.error('Please fill in all required fields'); return; }
        setSending(true);
        try {
            const res = await fetch(`${API}/api/public/contact`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form)
            });
            const data = await res.json();
            if (res.ok) { setSent(true); toast.success(data.message); }
            else { toast.error(data.detail || 'Failed to send message'); }
        } catch { toast.error('Network error. Please try again.'); }
        finally { setSending(false); }
    };

    return (
        <div className="min-h-screen bg-[#0A0D14]">
            <SEO title="Contact Us" description="Get in touch with Voxoora. Questions about audio guides, ticket bundles, or partnership opportunities? We'd love to hear from you." />
            <header className="fixed top-0 left-0 right-0 z-50 glass">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2 sm:gap-3">
                        {brand.logo_url ? (
                            <img src={brand.logo_url} alt={brand.platform_name} className="h-7 sm:h-9 max-w-[120px] sm:max-w-[160px] object-contain" />
                        ) : (
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-md bg-gold flex items-center justify-center">
                                <Headphones className="w-4 h-4 sm:w-5 sm:h-5 text-[#0A0D14]" />
                            </div>
                        )}
                        {!brand.logo_url && <span className="font-outfit font-semibold text-base sm:text-xl text-white">{brand.platform_name}</span>}
                    </Link>
                    <Link to="/"><Button variant="ghost" className="text-white/60 hover:text-white gap-2 text-sm"><ArrowLeft className="w-4 h-4" /> Back</Button></Link>
                </div>
            </header>

            <div className="pt-24 sm:pt-32 pb-16 px-4 sm:px-6">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-10 sm:mb-14">
                        <p className="text-gold uppercase tracking-widest text-xs sm:text-sm mb-3">Get in Touch</p>
                        <h1 className="font-outfit text-3xl sm:text-5xl font-bold text-white mb-4">Contact Us</h1>
                        <p className="text-muted-foreground max-w-xl mx-auto text-sm sm:text-base">Have a question, partnership inquiry, or need support? We'd love to hear from you.</p>
                    </div>

                    <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
                        {/* Contact Info */}
                        <div className="lg:col-span-2 space-y-6">
                            <div className="p-6 rounded-xl bg-[#121620] border border-white/10">
                                <h3 className="font-outfit font-semibold text-white mb-5 text-lg">Contact Information</h3>
                                <div className="space-y-5">
                                    <div className="flex items-start gap-3">
                                        <div className="w-9 h-9 rounded-lg bg-gold/10 flex items-center justify-center shrink-0"><Mail className="w-4 h-4 text-gold" /></div>
                                        <div>
                                            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Email</p>
                                            <a href="mailto:info@voxoora.com" className="text-sm text-white hover:text-gold transition-colors">info@voxoora.com</a>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div className="w-9 h-9 rounded-lg bg-gold/10 flex items-center justify-center shrink-0"><Phone className="w-4 h-4 text-gold" /></div>
                                        <div>
                                            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Phone</p>
                                            <p className="text-sm text-white">+39 06 123 4567</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div className="w-9 h-9 rounded-lg bg-gold/10 flex items-center justify-center shrink-0"><MapPin className="w-4 h-4 text-gold" /></div>
                                        <div>
                                            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Address</p>
                                            <p className="text-sm text-white">Rome, Italy</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="p-6 rounded-xl bg-[#121620] border border-white/10">
                                <h3 className="font-outfit font-semibold text-white mb-3 text-sm">For Business Partnerships</h3>
                                <p className="text-xs text-muted-foreground mb-4">Tour operators, DMCs, OTAs, and travel agents — explore our white-label solutions.</p>
                                <Link to="/register/operator"><Button variant="outline" size="sm" className="border-gold/30 text-gold hover:bg-gold/10 w-full">Become a Partner</Button></Link>
                            </div>
                        </div>

                        {/* Contact Form */}
                        <div className="lg:col-span-3">
                            <div className="p-6 sm:p-8 rounded-xl bg-[#121620] border border-white/10">
                                {sent ? (
                                    <div className="text-center py-10" data-testid="contact-success">
                                        <CheckCircle className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
                                        <h3 className="font-outfit text-xl font-semibold text-white mb-2">Message Sent!</h3>
                                        <p className="text-muted-foreground text-sm mb-6">Thank you for reaching out. We'll get back to you within 24 hours.</p>
                                        <Button onClick={() => { setSent(false); setForm({ name: '', email: '', subject: 'General Inquiry', message: '' }); }} variant="outline" className="border-white/10 text-white">Send Another Message</Button>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                                        {/* Honeypot - hidden from real users */}
                                        <div className="absolute opacity-0 h-0 overflow-hidden" aria-hidden="true" tabIndex={-1}>
                                            <input type="text" name="website" value={honeypot} onChange={e => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off" />
                                        </div>
                                        <div className="grid sm:grid-cols-2 gap-4">
                                            <div>
                                                <Label className="text-white/70 text-xs mb-1.5 block">Full Name *</Label>
                                                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="John Doe" className="bg-white/5 border-white/10 text-white" data-testid="contact-name" required />
                                            </div>
                                            <div>
                                                <Label className="text-white/70 text-xs mb-1.5 block">Email *</Label>
                                                <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="john@example.com" className="bg-white/5 border-white/10 text-white" data-testid="contact-email" required />
                                            </div>
                                        </div>
                                        <div>
                                            <Label className="text-white/70 text-xs mb-1.5 block">Subject</Label>
                                            <Select value={form.subject} onValueChange={v => setForm(p => ({ ...p, subject: v }))}>
                                                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="contact-subject"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="General Inquiry">General Inquiry</SelectItem>
                                                    <SelectItem value="Partnership">Partnership / B2B</SelectItem>
                                                    <SelectItem value="Support">Support / Help</SelectItem>
                                                    <SelectItem value="White Label">White Label Solutions</SelectItem>
                                                    <SelectItem value="Feedback">Feedback</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label className="text-white/70 text-xs mb-1.5 block">Message *</Label>
                                            <Textarea value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} placeholder="Tell us how we can help..." rows={5} className="bg-white/5 border-white/10 text-white resize-none" data-testid="contact-message" required />
                                        </div>
                                        <Button type="submit" disabled={sending} className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2 w-full sm:w-auto" data-testid="contact-submit">
                                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {sending ? 'Sending...' : 'Send Message'}
                                        </Button>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
